import Button from "@afa-shared/afa-components/dist/Buttons/Button";
import { StandardLink } from "@components/Link/StandardLink";
import Message from "@components/Message/Message";
import { setQuizModalClosed, setQuizModalOpen } from "@utils/globalClassHelper";
import { RefObject, useEffect, useRef, useState } from "react";

import QuizCompetitionModal from "../../Modals/QuizCompetitionModal/QuizCompetitionModal";
import QuizConclusionModal from "../../Modals/QuizConclusionModal/QuizConclusionModal";
import QuizStep from "../../QuizStep/QuizStep";
import { QuizBlockType_quizBlock_questionAreaCollection_items } from "../../queries/contentfulTypes/QuizBlockType";
import { getQuizResult, getQuizResultImage } from "../ResultHelper";
import { ResultModel } from "../ResultModel";
import quizResultStyles from "./QuizResult.module.css";

const defaultResultModel: ResultModel = {
  insufficient: {
    message:
      "Allt börjar med ett första steg! Passa gärna på att lära dig mer om kollektivavtalade försäkringar som kan hjälpa dig om du blir sjuk. Eller skadad på jobbet. Snart har du hur bra koll som helst!",
  },
  average: {
    message:
      "Inte illa! Du är på god väg att få riktigt bra koll! Passa gärna på att lära dig mer om kollektivavtalade försäkringar som kan hjälpa dig om du blir sjuk. Eller skadad på jobbet.",
  },
  great: {
    message:
      "Här har vi en kämpe! Bättre koll än de flesta, vilket du ska vara stolt över. Passa gärna på att lära dig mer om kollektivavtalade försäkringar som kan hjälpa dig om du blir sjuk. Eller skadad på jobbet.",
  },
  perfect: {
    message:
      "Kolla vem som hade koll! Du hade alla rätt och är därmed en mästare på kollektivavtalade försäkringar!",
    competitionText:
      "Vi lottar ut 100 t-shirtar bland alla som fick full pott i quizet. T-shirtarna är i One Size motsvarandes storlek XL. Om du vill delta i utlottningen kan du gå vidare till nästa steg och fylla i dina uppgifter.",
  },
  contactFeedback: {
    heading: "Tack för att du gjorde vårt quiz!",
    message: (
      <>
        Om du är en av de lyckliga som får vår t-shirt så skickar vi ut den så fort som möjligt. När
        vi hanterat e-postkonversationen raderar vi all e-post. Här kan du läsa mer om{" "}
        <StandardLink
          hideIcon={true}
          href={"/kundtjanst/information-for-olika-kategorier/deltagare-vid-utbildning-eller-event"}
          text={"hur vi behandlar dina personuppgifter"}
          className={quizResultStyles.inlineLink}
        />
        .
      </>
    ),
  },
};

interface IQuizResult {
  questions: Array<QuizBlockType_quizBlock_questionAreaCollection_items>;
  userQuizAnswers: Array<string>;
  resetQuiz: () => void;
  quizConclusions: Array<string>;
  userCorrectAnswers: number;
  resultInsufficientMessage: string | null | undefined;
  resultBronzeMessage: string | null | undefined;
  resultSilverMessage: string | null | undefined;
  resultGoldMessage: string | null | undefined;
  bronzeImage: any;
  silverImage: any;
  goldImage: any;
  resultPageContactButtonText: string | null | undefined;
  resultPageConclusionButtonText: string | null | undefined;
  availableQuizForm: boolean;
  quizId: string;
  showForm: boolean;
  containerRef: RefObject<HTMLDivElement>;
  quizType: string;
}

const QuizResult = ({
  questions,
  userQuizAnswers,
  resetQuiz,
  quizConclusions,
  userCorrectAnswers,
  resultInsufficientMessage,
  resultBronzeMessage,
  resultSilverMessage,
  resultGoldMessage,
  bronzeImage,
  silverImage,
  goldImage,
  resultPageContactButtonText,
  resultPageConclusionButtonText,
  availableQuizForm,
  quizId,
  showForm,
  containerRef,
}: IQuizResult) => {
  const [conclusionModalOpen, setConclusionModalOpen] = useState(false);
  const [competitionModalOpen, setCompetitionModalOpen] = useState(false);
  const [formIsSubmitted, setFormIsSubmitted] = useState<boolean>(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const sumbitionFeedbackRef = useRef(null);
  const defaultResultMessageModel = getQuizResult(
    userCorrectAnswers,
    questions?.length,
    defaultResultModel,
    resultInsufficientMessage,
    resultBronzeMessage,
    resultSilverMessage,
    resultGoldMessage,
  );
  const resultImage = getQuizResultImage(
    userCorrectAnswers,
    questions?.length,
    bronzeImage,
    silverImage,
    goldImage,
  );

  const handleModalToggle = (setOpen: boolean, modalName: string) => {
    if (setOpen) {
      const currentScroll = window?.scrollY;
      setScrollPosition(currentScroll);
      sessionStorage.setItem(`${quizId}-modal-open`, "true");
      setQuizModalOpen();
      document.body.style.top = `-${currentScroll}px`;
    } else {
      document.body.style.top = "";
      setQuizModalClosed();
      window?.scrollTo(0, scrollPosition);
      containerRef?.current?.focus();
      sessionStorage.setItem(`${quizId}-modal-open`, "false");
    }
    modalName === "competition"
      ? setCompetitionModalOpen(setOpen)
      : setConclusionModalOpen(setOpen);
  };

  useEffect(() => {
    if (
      showForm &&
      userCorrectAnswers === questions?.length &&
      availableQuizForm &&
      !formIsSubmitted
    ) {
      handleModalToggle(true, "competition");
    } else {
      containerRef?.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buttons = (isNotebookDown: boolean) => {
    const showCompetitionBtn =
      userCorrectAnswers === questions?.length && availableQuizForm && !formIsSubmitted;

    return (
      <>
        {!formIsSubmitted && (
          <div
            className={`${quizResultStyles.buttonWrapper} ${
              isNotebookDown ? "" : quizResultStyles.desktopWrapper
            }`}
          >
            {showCompetitionBtn && (
              <Button
                buttonType="primary"
                onClick={() => {
                  handleModalToggle(true, "competition");
                }}
                label={
                  resultPageContactButtonText ? resultPageContactButtonText : "Fyll i din adress"
                }
              />
            )}

            <Button
              buttonType={showCompetitionBtn ? "secondary" : "primary"}
              onClick={() => {
                resetQuiz();
              }}
              label={"Gör om quizet"}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className={quizResultStyles.quizResult}>
      <QuizStep
        ivImage={resultImage}
        children={
          <>
            <Message
              heading={
                formIsSubmitted
                  ? defaultResultModel?.contactFeedback?.heading
                  : `Ditt resultat: ${userCorrectAnswers} av ${questions?.length}`
              }
              headingVariant={"h3"}
              type={"success"}
              children={
                <>
                  <p>
                    {formIsSubmitted
                      ? defaultResultModel?.contactFeedback?.message
                      : defaultResultMessageModel?.message}
                  </p>
                  {!formIsSubmitted && (
                    <button
                      type="button"
                      className={quizResultStyles.conclusionLink}
                      onClick={() => handleModalToggle(true, "conclusion")}
                    >
                      {resultPageConclusionButtonText || "Se dina svar"}
                    </button>
                  )}
                </>
              }
              ref={sumbitionFeedbackRef}
            />
            {buttons(false)}
          </>
        }
        notebookDownButtonWrapper={buttons(true)}
      />
      {conclusionModalOpen && (
        <QuizConclusionModal
          questions={questions}
          userQuizAnswers={userQuizAnswers}
          userCorrectAnswers={userCorrectAnswers}
          conclusions={quizConclusions}
          handleModalClose={() => handleModalToggle(false, "conclusion")}
          containerRef={containerRef}
        />
      )}
      {competitionModalOpen && (
        <QuizCompetitionModal
          handleModalClose={() => handleModalToggle(false, "competition")}
          setFormIsSubmitted={() => setFormIsSubmitted(true)}
          quizId={quizId}
          containerRef={containerRef}
        />
      )}
    </div>
  );
};

export default QuizResult;
